import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { isNativeApp, isNativePushEnabled } from '../utils/platform'
import { env } from '../config/env'
import { registerForPush, setPushNavigationHandler } from '../services/push-registration'
import { setAppLinkNavigationHandler } from '../utils/app-links'
import { pushDiagnostics } from '../stores/push-diagnostics-store'

/**
 * Drives native push registration after sign-in. All of the real work lives in
 * the React-independent `push-registration` service singleton; this hook only:
 *   - records build/runtime gating flags for the on-device diagnostic,
 *   - installs the deep-link navigation handler,
 *   - kicks off registration when a user is available and on every app
 *     foreground (with the service de-duping concurrent runs).
 *
 * Registration is intentionally NOT coupled to a long-lived effect closure, so
 * auth timing or a stalled native call can no longer strand it.
 */
export function usePushNotifications(userId: string | undefined) {
  const navigate = useNavigate()

  // Record build/runtime gating flags up front so the on-device diagnostic
  // shows WHY push is (or isn't) active even before any token arrives.
  useEffect(() => {
    if (!isNativeApp()) return
    pushDiagnostics.setEnvironment({
      platform: Capacitor.getPlatform(),
      isNativeApp: isNativeApp(),
      enablePushFlag: env.enablePush,
      nativePushCompiled:
        typeof __NATIVE_PUSH_ENABLED__ !== 'undefined' && __NATIVE_PUSH_ENABLED__,
      isNativePushEnabled: isNativePushEnabled(),
    })
  }, [])

  // In-app navigation for links that point at this app (chat messages, every
  // platform — web included).
  useEffect(() => {
    setAppLinkNavigationHandler((path) => navigate(path))
    return () => setAppLinkNavigationHandler(null)
  }, [navigate])

  // Keep the deep-link navigation handler current for notification taps.
  useEffect(() => {
    if (!isNativeApp()) return
    setPushNavigationHandler((path) => navigate(path))
    return () => setPushNavigationHandler(null)
  }, [navigate])

  // Register once a user is available, and re-attempt on every app foreground.
  useEffect(() => {
    if (!isNativeApp()) return
    if (!userId) return
    if (!isNativePushEnabled()) {
      console.warn(
        '[push] Native push disabled at build time — rebuild with google-services.json + VITE_ENABLE_PUSH=true',
      )
      pushDiagnostics.note(
        'push gated off — isNativePushEnabled() is false (missing google-services.json or VITE_ENABLE_PUSH at build)',
      )
      return
    }

    let cancelled = false
    void registerForPush({ reason: 'sign-in' })

    let removeResume: (() => void) | null = null
    void (async () => {
      try {
        const { App } = await import('@capacitor/app')
        const resumeListener = await App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive || cancelled) return
          void registerForPush({ reason: 'foreground' })
        })
        if (cancelled) {
          void resumeListener.remove()
          return
        }
        removeResume = () => void resumeListener.remove()
      } catch {
        /* @capacitor/app unavailable — foreground retry simply won't fire */
      }
    })()

    return () => {
      cancelled = true
      removeResume?.()
    }
  }, [userId])
}
